import React, { useEffect, useState } from "react";
import { api, fmtNum, fmtDate, errMsg } from "@finance/lib/api";
import Layout, { PageHeader } from "@finance/components/Layout";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@finance/components/ui/select";
import { DateField } from "@finance/components/DateField";
import { Label } from "@finance/components/ui/label";
import { EmptyState } from "@finance/components/DesignSystem";
import { Airplane, MapPin } from "@phosphor-icons/react";

const STATUS_CLS = {
  Approved: "bg-emerald-50 text-emerald-700", Paid: "bg-blue-50 text-blue-700",
  Rejected: "bg-red-50 text-red-600", Pending: "bg-amber-50 text-amber-700",
};
const TRIP_CLS = {
  Planned: "bg-gray-100 text-gray-700", Active: "bg-emerald-50 text-emerald-700",
  Completed: "bg-blue-50 text-blue-700", Cancelled: "bg-red-50 text-red-600",
};

export default function TripReports() {
  const [trips, setTrips] = useState([]);
  const [rows, setRows] = useState([]);
  const [f, setF] = useState({ trip_id: "all", status: "all", date_from: "", date_to: "" });

  useEffect(() => { api.get("/trips").then((r) => setTrips(r.data)).catch(() => {}); }, []);
  const load = () => {
    const p = {};
    if (f.trip_id !== "all") p.trip_id = f.trip_id;
    if (f.status !== "all") p.status = f.status;
    if (f.date_from) p.date_from = f.date_from;
    if (f.date_to) p.date_to = f.date_to;
    api.get("/reports/trips", { params: p }).then((r) => setRows(r.data)).catch((e) => errMsg(e));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [f]);

  const grandTotal = rows.reduce((s, t) => s + (t.total_tzs || 0), 0);

  return (
    <Layout>
      <PageHeader title="Trip Reports" subtitle="Business Development trips and every expense logged against them" />
      <div className="p-8 space-y-5">
        <div className="grid grid-cols-1 gap-3 rounded-sm border border-gray-200 bg-white p-4 md:grid-cols-4" data-testid="trip-report-filters">
          <div>
            <Label className="lx-label">Trip</Label>
            <Select value={f.trip_id} onValueChange={(v) => setF({ ...f, trip_id: v })}>
              <SelectTrigger className="mt-1" data-testid="trip-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All trips</SelectItem>
                {trips.map((t) => <SelectItem key={t.id} value={t.id}>{t.trip_no} · {t.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="lx-label">Status</Label>
            <Select value={f.status} onValueChange={(v) => setF({ ...f, status: v })}>
              <SelectTrigger className="mt-1" data-testid="trip-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent>{["all", "Planned", "Active", "Completed", "Cancelled"].map((s) => <SelectItem key={s} value={s}>{s === "all" ? "All statuses" : s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="lx-label">Expenses from</Label><DateField className="mt-1" data-testid="trip-date-from" value={f.date_from} onChange={(e) => setF({ ...f, date_from: e.target.value })} /></div>
          <div><Label className="lx-label">Expenses to</Label><DateField className="mt-1" data-testid="trip-date-to" value={f.date_to} onChange={(e) => setF({ ...f, date_to: e.target.value })} /></div>
        </div>

        {rows.length > 0 && (
          <div className="flex items-center justify-between rounded-sm border border-gray-200 bg-white px-5 py-3" data-testid="trip-report-summary">
            <span className="text-sm text-gray-500">{rows.length} trip(s)</span>
            <span className="lx-num text-lg font-bold text-gray-900">Total: TSh {fmtNum(grandTotal)}</span>
          </div>
        )}

        {rows.length === 0 ? (
          <EmptyState icon={Airplane} title="No trips match" message="Adjust the filters, or create a trip in the HR module." />
        ) : rows.map((t) => (
          <div key={t.id} className="overflow-hidden rounded-sm border border-gray-200 bg-white" data-testid={`trip-report-${t.id}`}>
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 bg-gray-50 px-5 py-3">
              <div>
                <div className="font-display text-sm font-bold text-gray-900">{t.trip_no} · {t.name}
                  <span className={`ml-2 rounded-sm px-1.5 py-0.5 text-[11px] font-medium ${TRIP_CLS[t.status] || "bg-gray-100 text-gray-700"}`}>{t.status}</span>
                </div>
                <div className="mt-0.5 flex flex-wrap items-center gap-3 text-xs text-gray-500">
                  <span>{fmtDate(t.start_date) || "—"} → {fmtDate(t.end_date) || "—"}</span>
                  {(t.locations || []).length > 0 && <span className="inline-flex items-center gap-1"><MapPin size={12} />{t.locations.join(", ")}</span>}
                  {(t.cost_codes || []).length > 0 && <span>Codes: {t.cost_codes.join(", ")}</span>}
                </div>
              </div>
              <div className="text-right">
                <div className="lx-num text-base font-bold text-gray-900">TSh {fmtNum(t.total_tzs)}</div>
                <div className="text-xs text-gray-400">{t.expense_count} expense(s)</div>
              </div>
            </div>
            {Object.keys(t.status_breakdown || {}).length > 0 && (
              <div className="flex flex-wrap gap-2 border-b border-gray-100 px-5 py-2">
                {Object.entries(t.status_breakdown).map(([st, b]) => (
                  <span key={st} className={`rounded-sm px-2 py-0.5 text-[11px] font-medium ${STATUS_CLS[st] || "bg-gray-100 text-gray-700"}`}>{st}: {b.count} · TSh {fmtNum(b.amount_tzs)}</span>
                ))}
              </div>
            )}
            {t.expenses.length === 0 ? (
              <div className="px-5 py-6 text-center text-sm text-gray-400">No expenses logged against this trip yet.</div>
            ) : (
              <table className="w-full text-sm">
                <thead><tr className="lx-label border-b border-gray-100 text-gray-400"><th className="px-5 py-2 text-left">Date</th><th className="px-5 py-2 text-left">Description</th><th className="px-5 py-2 text-left">Category</th><th className="px-5 py-2 text-left">Submitted by</th><th className="px-5 py-2 text-right">Amount</th><th className="px-5 py-2 text-center">Status</th></tr></thead>
                <tbody>
                  {t.expenses.map((e) => (
                    <tr key={e.id} className="border-b border-gray-50" data-testid={`trip-exp-${e.id}`}>
                      <td className="px-5 py-2 lx-num whitespace-nowrap">{fmtDate(e.date)}</td>
                      <td className="px-5 py-2">{e.description || "—"}</td>
                      <td className="px-5 py-2 text-gray-600">{e.category || "—"}</td>
                      <td className="px-5 py-2 text-gray-600">{e.submitted_by || e.employee || "—"}</td>
                      <td className="px-5 py-2 text-right lx-num">{e.currency} {fmtNum(e.amount)}</td>
                      <td className="px-5 py-2 text-center"><span className={`rounded-sm px-1.5 py-0.5 text-[11px] font-medium ${STATUS_CLS[e.status] || "bg-amber-50 text-amber-700"}`}>{e.status}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        ))}
      </div>
    </Layout>
  );
}
